import { useTenant } from "@/hooks/useTenant";
import { portalFetch } from "@/lib/portalApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

/**
 * Fetches all settings for the current tenant as a flat record.
 * Multiple `useSettingList` calls share this single query via React Query cache.
 */
function useAllSettings() {
  const { currentTenantId } = useTenant();

  return useQuery({
    queryKey: ["portal-settings", currentTenantId],
    queryFn: () =>
      portalFetch<Record<string, unknown>>(`/api/portal/tenants/${currentTenantId}/settings`),
    enabled: !!currentTenantId,
  });
}

/**
 * Drop-in replacement for the old localStorage-backed hook.
 * Returns `{ items, isLoading, save }` — same signature as before.
 */
export function useSettingList(key: string) {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();
  const { data, isLoading } = useAllSettings();

  const raw = data?.[key];
  const items: string[] = Array.isArray(raw) ? (raw as string[]) : [];

  const mutation = useMutation({
    mutationFn: (value: string[]) =>
      portalFetch(`/api/portal/tenants/${currentTenantId}/settings/${key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, key, value }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-settings"] }),
  });

  const save = useCallback(
    (value: string[]) => {
      mutation.mutate(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on the stable `mutation.mutate` reference, not the whole `mutation` object which is recreated every render.
    [mutation.mutate],
  );

  return { items, isLoading, save };
}

/**
 * Single-value setting (e.g. default_group_id).
 * Returns `{ value, isLoading, save }`.
 */
export function useSetting(key: string) {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();
  const { data, isLoading } = useAllSettings();

  const raw = data?.[key];
  const value: string | null = typeof raw === "string" ? raw : null;

  const mutation = useMutation({
    mutationFn: (newValue: string | null) =>
      portalFetch(`/api/portal/tenants/${currentTenantId}/settings/${key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, key, value: newValue ?? "" }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-settings"] }),
  });

  const save = useCallback(
    (newValue: string | null) => {
      mutation.mutate(newValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on the stable `mutation.mutate` reference, not the whole `mutation` object which is recreated every render.
    [mutation.mutate],
  );

  return { value, isLoading, save };
}
